import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '@/lib/cn'

import { sectionOf, useSections, type Shelf } from './catalogue'
import { categoryPath, useRoute } from './router'
import { useStore } from './state'
import { Link } from './ui'

const ALL = 'all'

/** Which tab the page stands under: the catalogue, a top category, or none. */
function useActive(sections: Shelf[] | undefined): string | null {
  const route = useRoute()
  const categories = useStore((state) => state.categories)
  if (route.name === 'home') return ALL
  if (!categories || !sections) return null
  let id: number | null = null
  if (route.name === 'category') id = route.id
  if (route.name === 'package') {
    id =
      categories.flatMap((category) => category.packages ?? []).find((each) => each.id === route.id)
        ?.category.id ?? null
  }
  const section = id === null ? null : sectionOf(sections, categories, id)
  return section === null ? null : String(section)
}

/**
 * The categories in the store's header, with a pill that slides to the tab under the
 * pointer and back to the page's own when it leaves.
 */
export function CategoryNav({ className }: { className?: string }) {
  const shelves = useSections()
  const active = useActive(shelves)
  const [hovered, setHovered] = useState<string | null>(null)
  const list = useRef<HTMLUListElement | null>(null)
  const tabs = useRef(new Map<string, HTMLAnchorElement>())
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null)
  const target = hovered ?? active

  const measure = useCallback(() => {
    const tab = target ? tabs.current.get(target) : undefined
    setPill(tab ? { left: tab.offsetLeft, width: tab.offsetWidth } : null)
  }, [target])

  useLayoutEffect(measure, [measure, shelves?.length])

  useEffect(() => {
    window.addEventListener('resize', measure)
    // The display font arrives after the first paint and widens every tab.
    void document.fonts?.ready.then(measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure])

  // On a phone the row scrolls: the page's own tab comes into view.
  useEffect(() => {
    const tab = active ? tabs.current.get(active) : undefined
    const row = list.current?.parentElement
    if (!tab || !row || row.scrollWidth <= row.clientWidth) return
    row.scrollTo({
      left: tab.offsetLeft - row.clientWidth / 2 + tab.offsetWidth / 2,
      behavior: 'smooth',
    })
  }, [active, shelves?.length])

  if (!shelves || shelves.length === 0) return null

  const items = [
    { key: ALL, label: 'Tutto', to: '/' },
    ...shelves.map(({ category }) => ({
      key: String(category.id),
      label: category.name,
      to: categoryPath(category),
    })),
  ]

  return (
    <nav aria-label="Categorie" className={cn('no-scrollbar overflow-x-auto', className)}>
      <ul
        ref={list}
        className="relative flex w-max items-center gap-0.5"
        onPointerLeave={() => setHovered(null)}
      >
        <span
          aria-hidden
          className="tab-pill"
          style={{
            transform: `translateX(${pill?.left ?? 0}px)`,
            width: pill?.width ?? 0,
            opacity: pill ? 1 : 0,
          }}
        />
        {items.map((item) => (
          <li key={item.key}>
            <Link
              to={item.to}
              ref={(node) => {
                if (node) tabs.current.set(item.key, node)
                else tabs.current.delete(item.key)
              }}
              onPointerEnter={() => setHovered(item.key)}
              onFocus={() => setHovered(item.key)}
              onBlur={() => setHovered(null)}
              aria-current={item.key === active ? 'page' : undefined}
              className={cn(
                'relative z-10 block rounded-full px-3.5 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-300',
                item.key === target ? 'text-white' : 'text-mist hover:text-white',
              )}
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}
